import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";

const AdminUserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const currentUser = useSelector(state => state.user.data);
  
  const [userInfo, setUserInfo] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRecipes: 0,
    avgCarbonFootprint: 0,
    totalCarbonFootprint: 0
  });

  useEffect(() => {
    // Fetch user information
    const fetchUserInfo = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/user`);
        const users = await response.json();
        const user = users.find(u => u.id === parseInt(userId));
        setUserInfo(user);
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
    };

    // Fetch user's recipes
    const fetchUserRecipes = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/user/${userId}/recipe`
        );
        const data = await response.json();
        setRecipes(data);
        
        // Calculate statistics
        if (data.length > 0) {
          const totalCarbon = data.reduce((sum, recipe) => sum + (recipe.carbonFootprint || 0), 0);
          const avgCarbon = totalCarbon / data.length;
          
          setStats({
            totalRecipes: data.length,
            avgCarbonFootprint: avgCarbon.toFixed(2),
            totalCarbonFootprint: totalCarbon.toFixed(2)
          });
        }
      } catch (error) {
        console.error("Error fetching recipes:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
    fetchUserRecipes();
  }, [userId]);

  const deleteRecipe = async (recipeId) => {
    const proceed = window.confirm("Are you sure you want to delete this meal?");
    if (proceed) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/recipe/${recipeId}`,
          { method: "DELETE" }
        );
        if (response.ok) {
          setRecipes(recipes.filter(recipe => recipe.id !== recipeId));
          // Recalculate stats
          const newRecipes = recipes.filter(recipe => recipe.id !== recipeId);
          if (newRecipes.length > 0) {
            const totalCarbon = newRecipes.reduce((sum, recipe) => sum + (recipe.carbonFootprint || 0), 0);
            setStats({
              totalRecipes: newRecipes.length,
              avgCarbonFootprint: (totalCarbon / newRecipes.length).toFixed(2),
              totalCarbonFootprint: totalCarbon.toFixed(2)
            });
          } else {
            setStats({ totalRecipes: 0, avgCarbonFootprint: 0, totalCarbonFootprint: 0 });
          }
        }
      } catch (error) {
        console.error("Error deleting recipe:", error);
      }
    }
  };

  const getCarbonLevel = (carbonFootprint) => {
    if (carbonFootprint < 1) return { label: "Low", color: "green" };
    if (carbonFootprint < 3) return { label: "Medium", color: "yellow" };
    return { label: "High", color: "red" };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-8 max-w-6xl">
      <div className="py-8">
        {/* Header with back button */}
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center text-blue-600 hover:text-blue-800"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Users
          </button>
        </div>

        {/* User Info Card */}
        {userInfo && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center mb-4">
              <div className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center text-white text-2xl font-bold mr-4">
                {userInfo.email.charAt(0).toUpperCase()}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{userInfo.email}</h2>
                <div className="flex items-center mt-1">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    userInfo.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {userInfo.role === 'admin' ? '👑 Admin' : 'User'}
                  </span>
                  <span className="ml-3 text-sm text-gray-500">
                    Joined: {new Date(userInfo.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">Total Meals</div>
                <div className="text-3xl font-bold text-blue-600">{stats.totalRecipes}</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">Avg Carbon (kg CO₂)</div>
                <div className="text-3xl font-bold text-green-600">{stats.avgCarbonFootprint}</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">Total Carbon (kg CO₂)</div>
                <div className="text-3xl font-bold text-orange-600">{stats.totalCarbonFootprint}</div>
              </div>
            </div>
          </div>
        )}

        {/* Recipes List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h3 className="text-xl font-bold text-gray-800">Created Meals</h3>
          </div>

          {recipes.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p className="text-lg">This user hasn't created any meals yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Meal Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Servings
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Carbon (kg CO₂)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Impact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recipes.map((recipe) => {
                    const carbonLevel = getCarbonLevel(recipe.carbonFootprint || 0);
                    return (
                      <tr key={recipe.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {recipe.name}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-500 max-w-xs truncate">
                            {recipe.description || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {/* Parse ingredients JSON to get servings if available */}
                            {recipe.servings || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {(recipe.carbonFootprint || 0).toFixed(2)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-${carbonLevel.color}-100 text-${carbonLevel.color}-800`}>
                            {carbonLevel.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(recipe.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <Link
                            to={`/recipes/edit/${recipe.id}`}
                            className="text-indigo-600 hover:text-indigo-900 mr-3"
                          >
                            View
                          </Link>
                          <button
                            onClick={() => deleteRecipe(recipe.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUserDetails;
